import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createPeripheral, updatePeripheral } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { PeripheralType, SitePeripheralDTO } from "src/app/models/Sites/sitePeripheralDTO";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { FormUtils } from "src/app/shared/utils/formUtils";

//Peripheral Detail Form
const PERIPHERAL_FORM_PARAMS = {
  enabled: "enabled",
  peripheralNumber: "peripheralNumber",
  serialNumber: "serialNumber",
  description: "description",
  peripheralTypeCode: "peripheralTypeCode",
  printerMaxColumns: "printerMaxColumns", 
  prefixImg: "prefixImg",
  additionalData: "additionalData",
  protocol: "protocol"
};

@Component({
  selector: "app-peripheral-form-dialog",
  templateUrl: "./peripheral-form-dialog.component.html",
  styleUrls: ["./peripheral-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PeripheralFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getPeripheralForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;
  
  siteCode: string;
  currentPeripheral: SitePeripheralDTO;

  peripheralTypesList: PeripheralType[];

  peripheralDataForm: FormGroup;

  isCreate: boolean = false;

  printerMaxColumnsValues: number[] = [20, 32, 40, 48];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PeripheralFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }
  
  ngOnInit(): void {
    this.setDialogData();
    this.createPeripheralForm();
    this.setFormValues(this.currentPeripheral);
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentPeripheral = this.data.currentPeripheral;
    this.peripheralTypesList = this.data.peripheralTypesList;
  }

  createPeripheralForm() {
    this.peripheralDataForm = this.fb.group({
      [PERIPHERAL_FORM_PARAMS.enabled]: [true],
      [PERIPHERAL_FORM_PARAMS.peripheralNumber]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [PERIPHERAL_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [PERIPHERAL_FORM_PARAMS.description]: [null, Validators.maxLength(45)],
      [PERIPHERAL_FORM_PARAMS.additionalData]: [null, [FormUtils.jsonValidator]],
      [PERIPHERAL_FORM_PARAMS.peripheralTypeCode]: [null, [Validators.required]],
      [PERIPHERAL_FORM_PARAMS.printerMaxColumns]: null,
      [PERIPHERAL_FORM_PARAMS.prefixImg]: null,
      [PERIPHERAL_FORM_PARAMS.protocol]: this.fb.group({}),
    });
    this.subscribeFormChanges(this.peripheralDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getPeripheralFormPristine();
    });
  }

  setFormValues(peripheral: SitePeripheralDTO, duplication?: boolean) {
    this.peripheralDataForm.controls.enabled.setValue(
      peripheral?.enabled ?? true
    );
    this.peripheralDataForm.controls.peripheralNumber.setValue(
      duplication ? null : peripheral?.peripheralNumber ?? null
    );
    this.peripheralDataForm.controls.serialNumber.setValue(
      duplication ? null : peripheral?.serialNumber ?? null
    );
    this.peripheralDataForm.controls.description.setValue(
      peripheral?.description ?? null
    );
    this.peripheralDataForm.controls.additionalData.setValue(
      peripheral?.additionalData ? JSON.stringify(peripheral.additionalData, null, "\t") : null
    );
    this.peripheralDataForm.controls.peripheralTypeCode.setValue(
      peripheral?.protocolTypeCode ?? null
    );
    this.peripheralDataForm.controls.printerMaxColumns.setValue(
      Number(peripheral?.additionalData?.printerMaxCols) ?? null
    );
    this.peripheralDataForm.controls.prefixImg.setValue(
      peripheral?.additionalData?.prefixImg ?? null
    );
    setProtocolFormValues(this.peripheralDataForm, peripheral);
  }

  duplicate() {
    this.setFormValues(this.currentPeripheral, true);

    this.isCreate = true;
    this.currentPeripheral = null;
  }

  onSubmit() {
    if (!this.peripheralDataForm.valid) {
      this.peripheralDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (this.peripheralDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const protocolFormGroup = this.peripheralDataForm.get(
      PERIPHERAL_FORM_PARAMS.protocol
    ) as FormGroup;

    let printerOptions = {
      printerMaxCols: this.peripheralDataForm.controls.printerMaxColumns.value ?? null,
      prefixImg: this.peripheralDataForm.controls.prefixImg.value ?? null,
    };

    let peripheral: SitePeripheralDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentPeripheral?.code,
      enabled: this.peripheralDataForm.controls.enabled.value ?? false,
      peripheralNumber: this.peripheralDataForm.controls.peripheralNumber.value ?? null,
      serialNumber: this.peripheralDataForm.controls.serialNumber.value ?? null,
      description: this.peripheralDataForm.controls.description.value ?? null,
      additionalData: 
        this.peripheralDataForm.controls.peripheralTypeCode.value === 'PRINTER'
        ? printerOptions
        : this.peripheralDataForm.controls.additionalData.value
        ? JSON.parse(this.peripheralDataForm.controls.additionalData.value)
        : null,
      protocolTypeCode: this.peripheralDataForm.controls.peripheralTypeCode.value ?? null,
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
    };

    if (this.isCreate) {
      this.store.dispatch(createPeripheral({ peripheral }));
    } else {
      this.store.dispatch(updatePeripheral({ peripheral }));
    }

    this.peripheralDataForm.markAsUntouched();
    this.peripheralDataForm.markAsPristine();
    this.getPeripheralFormPristine();
  }

  getPeripheralFormPristine() {
    this.getPeripheralForm.emit(this.peripheralDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.peripheralDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
